'use client';

import { useCallback, useRef, useState } from 'react';
import { Play, Pause } from 'lucide-react';

interface VideoPlayerProps {
  src: string;
  title: string;
  /** Used in aria-label and as visible caption (optional) */
  caption?: string;
  /** Aspect ratio class — Tailwind aspect-video by default */
  aspectClass?: string;
  /** Override container styles */
  className?: string;
  /** Optional poster image */
  poster?: string;
  /** Variant tunes the play button colour for dark backgrounds */
  variant?: 'light' | 'dark';
}

export function VideoPlayer({
  src,
  title,
  caption,
  aspectClass = 'aspect-video',
  className = '',
  poster,
  variant = 'light',
}: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    setHasInteracted(true);
    if (video.paused) {
      video.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    } else {
      video.pause();
      setPlaying(false);
    }
  }, []);

  const buttonBg =
    variant === 'dark'
      ? 'bg-white text-sandron-ink-900 hover:bg-sandron-ink-50'
      : 'bg-sandron-ink-900 text-white hover:bg-sandron-ink-700';

  return (
    <figure className={className}>
      <div
        className={`group relative ${aspectClass} overflow-hidden rounded-3xl border border-sandron-ink-100 bg-sandron-ink-50`}
      >
        <video
          ref={videoRef}
          src={src}
          poster={poster}
          preload="metadata"
          playsInline
          controls={hasInteracted}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => setPlaying(false)}
          aria-label={title}
          className="h-full w-full object-cover"
        >
          <track kind="captions" srcLang="en" label="English" />
          Your browser does not support the video tag.
        </video>

        {!playing && !hasInteracted && (
          <button
            type="button"
            onClick={togglePlay}
            aria-label={`Play video: ${title}`}
            className="absolute inset-0 flex items-center justify-center bg-sandron-ink-900/10 transition-colors duration-200 hover:bg-sandron-ink-900/20 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-spacenest-primary"
          >
            <span
              className={`flex h-20 w-20 items-center justify-center rounded-full shadow-[0_18px_50px_-12px_rgba(11,18,32,0.4)] transition-transform duration-200 group-hover:scale-105 ${buttonBg}`}
            >
              <Play size={28} strokeWidth={2} fill="currentColor" />
            </span>
          </button>
        )}

        {playing && hasInteracted && (
          <button
            type="button"
            onClick={togglePlay}
            aria-label={`Pause video: ${title}`}
            className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-sandron-ink-900/80 text-white opacity-0 transition-opacity duration-200 focus-visible:opacity-100 group-hover:opacity-100"
          >
            <Pause size={16} />
          </button>
        )}
      </div>
      {caption && (
        <figcaption className="mt-3 text-center text-sm text-sandron-ink-500">
          {caption}
        </figcaption>
      )}
    </figure>
  );
}
